import { ErrorHandler, Injectable } from '@angular/core';
import { createErrorHandler } from '@sentry/angular';
import { CrashlyticsService } from '../crashlytics.service';

/**
 * Unified global error handler.
 *
 * Forwards every unhandled Angular error to BOTH monitoring stacks:
 *   1. Sentry  — via the official @sentry/angular error handler (also preserves
 *                Angular's default console logging).
 *   2. Firebase Crashlytics — via CrashlyticsService.recordException() so native
 *                builds keep their existing crash reporting.
 *
 * Each bridge is guarded so a failure in one never suppresses the other, and
 * the handler itself never throws (which would break Angular's error pipeline).
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  /** Sentry's own ErrorHandler — no in-app dialog. */
  private readonly sentryHandler: ErrorHandler = createErrorHandler({ showDialog: false });

  private readonly crashlytics: CrashlyticsService;

  constructor(crashlytics: CrashlyticsService) {
    this.crashlytics = crashlytics;
  }

  handleError(error: any): void {
    // 1. Forward to Sentry (which also re-logs to the console like Angular does).
    try {
      this.sentryHandler.handleError(error);
    } catch {
      // Fall back to a console log so the error is never fully swallowed.
      // eslint-disable-next-line no-console
      console.error(error);
    }

    // 2. Bridge to Firebase Crashlytics for native crash reporting.
    try {
      const message = this.extractMessage(error);
      void this.crashlytics.recordException(message);
    } catch { /* Crashlytics failures must not break error handling */ }
  }

  private extractMessage(error: any): string {
    if (!error) {
      return 'Unknown error';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error.stack) {
      return String(error.stack);
    }
    if (error.message) {
      return String(error.message);
    }
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
}
